/**
 * VÉRAULT Portal — Content Studio
 *
 * Editorial publishing suite for the advisor: journal management, media,
 * social, calendar, and (later) inline AI writing support. This layout
 * provides the persistent subnav across all /portal/advisor/content/*
 * pages and an editorial page header.
 *
 * Phase 1+2 ships: Dashboard · Journal · Calendar.
 * Phase 3-5 placeholders: Media · Social · AI Assistant.
 */
import React from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft,
  LayoutDashboard,
  BookOpen,
  CalendarDays,
  Images,
  Share2,
  Sparkles,
  Tag,
  Globe,
  FileText,
} from "lucide-react";
import { PageHeader, motion as portalMotion } from "../../../index";

const { fadeInUp, stagger } = portalMotion;

const TABS = [
  {
    to: "/portal/advisor/content",
    end: true,
    icon: LayoutDashboard,
    label: "Dashboard",
    testid: "content-tab-dashboard",
  },
  {
    to: "/portal/advisor/content/journal",
    icon: BookOpen,
    label: "Journal",
    testid: "content-tab-journal",
  },
  {
    to: "/portal/advisor/content/guides",
    icon: FileText,
    label: "Guides",
    testid: "content-tab-guides",
  },
  {
    to: "/portal/advisor/content/calendar",
    icon: CalendarDays,
    label: "Calendar",
    testid: "content-tab-calendar",
  },
  {
    to: "/portal/advisor/content/media",
    icon: Images,
    label: "Media",
    testid: "content-tab-media",
  },
  {
    to: "/portal/advisor/content/social",
    icon: Share2,
    label: "Social",
    testid: "content-tab-social",
  },
  {
    to: "/portal/advisor/content/campaigns",
    icon: Tag,
    label: "Campaigns",
    testid: "content-tab-campaigns",
  },
  {
    to: "/portal/advisor/content/assistant",
    icon: Sparkles,
    label: "AI Assistant",
    testid: "content-tab-assistant",
  },
  {
    to: "/portal/advisor/content/website",
    icon: Globe,
    label: "Website",
    testid: "content-tab-website",
  },
];

export default function ContentLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  // map URL → page-specific eyebrow/title/lede so the header is contextual
  const header = React.useMemo(() => {
    const path = location.pathname.replace(/\/+$/, "");
    if (path.endsWith("/journal")) {
      return {
        eyebrow: "Content Studio · Journal",
        title: "The editorial archive.",
        lede: "Long-form pieces that live on the public site as The Journal.",
      };
    }
    if (path.endsWith("/guides")) {
      return {
        eyebrow: "Content Studio · Guides",
        title: "Editorial mini-journals.",
        lede: "Curated destination guides that surface in clients' Travel Prep — matched by destination or assigned to specific trips.",
      };
    }
    if (path.endsWith("/calendar")) {
      return {
        eyebrow: "Content Studio · Calendar",
        title: "A quiet month at a glance.",
        lede: "Drafts, scheduled posts, and freshly published pieces, on a calm monthly grid.",
      };
    }
    if (path.endsWith("/media")) {
      return {
        eyebrow: "Content Studio · Media",
        title: "Your library.",
        lede: "Imagery, covers, and downloadable assets — organized by folder and destination.",
      };
    }
    if (path.endsWith("/social")) {
      return {
        eyebrow: "Content Studio · Social",
        title: "From journal to feed.",
        lede: "Draft captions for Instagram, LinkedIn, and email. Repurpose journal pieces with one click.",
      };
    }
    if (path.endsWith("/campaigns")) {
      return {
        eyebrow: "Content Studio · Campaigns",
        title: "Editorial collections.",
        lede: "Group journals, media, and social into themed collections — internal scaffolding or public series.",
      };
    }
    if (path.endsWith("/assistant")) {
      return {
        eyebrow: "Content Studio · AI Assistant",
        title: "The briefing room.",
        lede: "Read the rhythm, find the gaps, plan what to write next. Finished writing lives in the editor.",
      };
    }
    return {
      eyebrow: "Content Studio",
      title: "The publishing room.",
      lede: "Drafts, scheduled posts, the media library, and social repurposing — one calm surface.",
    };
  }, [location.pathname]);

  return (
    <motion.div
      initial="initial"
      animate="animate"
      variants={stagger}
      className="space-y-10"
      data-testid="content-studio-layout"
    >
      <motion.div variants={fadeInUp}>
        <button
          onClick={() => navigate("/portal/advisor")}
          className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--foreground-muted))] hover:text-[hsl(var(--accent-gold))] transition-colors mb-4"
          data-testid="content-back-to-dashboard"
        >
          <ArrowLeft className="size-3" strokeWidth={1.5} /> Back to dashboard
        </button>
        <PageHeader eyebrow={header.eyebrow} title={header.title} lede={header.lede} />
      </motion.div>

      {/* Persistent subnav */}
      <motion.nav
        variants={fadeInUp}
        className="flex flex-wrap items-center gap-x-1 gap-y-2 border-b border-[rgba(0,0,0,0.06)] -mt-2"
        data-testid="content-studio-subnav"
      >
        {TABS.map(({ to, icon: Icon, label, end, testid, soon }) => (
          <NavLink
            key={to}
            to={to}
            end={end}
            data-testid={testid}
            className={({ isActive }) =>
              `relative group inline-flex items-center gap-2 px-3.5 py-2.5 -mb-px transition-colors duration-200 ${
                isActive
                  ? "text-[hsl(var(--foreground))]"
                  : "text-[hsl(var(--foreground-muted))] hover:text-[hsl(var(--foreground))]"
              }`
            }
          >
            {({ isActive }) => (
              <>
                <Icon className="size-3.5 opacity-80" strokeWidth={1.5} />
                <span
                  className={`text-[13px] tracking-tight ${
                    isActive ? "font-serif" : "font-body"
                  }`}
                >
                  {label}
                </span>
                {soon && (
                  <span className="ml-0.5 font-mono text-[9px] uppercase tracking-[0.18em] text-[hsl(var(--accent-gold))]/70">
                    Soon
                  </span>
                )}
                <span
                  aria-hidden
                  className={`absolute left-2 right-2 -bottom-px h-px transition-colors duration-200 ${
                    isActive ? "bg-[hsl(var(--accent-gold))]" : "bg-transparent"
                  }`}
                />
              </>
            )}
          </NavLink>
        ))}
      </motion.nav>

      <motion.div variants={fadeInUp}>
        <Outlet />
      </motion.div>
    </motion.div>
  );
}
